import React, { useState, useEffect } from 'react';
import { Outlet, NavLink as RouterNavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthContext } from '../../hooks/useAuthContext';
import { supabase } from '../../config/supabase';
import { FiGrid, FiPackage, FiShoppingCart, FiCreditCard, FiDollarSign, FiTag, FiUsers, FiSettings, FiImage, FiLayers, FiFolder, FiLogOut, FiGlobe, FiTruck } from 'react-icons/fi';
import { FiBell, FiMenu, FiX } from 'react-icons/fi';

const AdminContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f9fafb;
  width: 100%;
  max-width: 100vw;
  overflow-x: clip;
  direction: ltr;
  text-align: left;
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.$isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 40;
  }
`;

const Sidebar = styled.aside`
  width: 260px;
  background: white;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  direction: ltr;
  text-align: left;
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 50;

  @media (max-width: 768px) {
    position: fixed;
    left: 0;
    top: 0;
    transform: translateX(${(props) => (props.$isOpen ? '0' : '-100%')});
    transition: transform 0.3s ease-in-out;
    box-shadow: ${(props) => (props.$isOpen ? '4px 0 15px rgba(0, 0, 0, 0.1)' : 'none')};
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
`;

const SidebarHeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoBox = styled.div`
  width: 48px;
  height: 48px;
  background: #5efba9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 18px;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
`;

const UserStatus = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: none;
  margin-left: auto;

  &:hover {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenuButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: none;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Nav = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: 20px 0;
  direction: ltr;
`;

const NavLink = styled(RouterNavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: #6b7280;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.2s;
  border-radius: 8px;
  margin: 0 12px;
  
  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }
  
  &.active {
    background: linear-gradient(135deg, #5efba9 0%, #4de89e 100%);
    color: #1f2937;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(94, 251, 169, 0.3);
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-x: visible;
  direction: ltr;
  text-align: left;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TopBar = styled.header`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 12px 16px;
    gap: 8px;
  }
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 15px;
    gap: 6px 8px;
  }
`;

const TitleText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;

  @media (max-width: 768px) {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const SuperAdminBadge = styled.span`
  background: #5efba9;
  color: #1f2937;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 3px 8px;
  }
`;

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    padding: 6px;
    font-size: 18px;
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  background: #5efba9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: white;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserNameTop = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #b91c1c;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 8px 10px;

    span {
      display: none;
    }
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 32px;
  overflow-y: auto;
  overflow-x: visible;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  direction: ltr;
  text-align: left;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export default function SuperAdminLayout() {
  const { profile, session } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const userInitials = profile?.first_name?.[0] || session?.user?.email?.[0] || 'S';

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      // Always clear local session first (reliable)
      const { error: localError } = await supabase.auth.signOut();
      if (localError) {
        console.error('Error signing out (local):', localError);
      }

      // Best-effort revoke (non-blocking)
      supabase.auth.signOut({ scope: 'global' }).catch(() => {});

      // Hard redirect so guards reset immediately
      window.location.assign('/superadmin/login');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out. Please try again.');
    } finally {
      setLoggingOut(false);
    }
  };

  // Get page title based on current route
  const getPageTitle = () => {
    if (location.pathname === '/superadmin') return 'Dashboard';
    if (location.pathname === '/superadmin/products') return 'All Products';
    if (location.pathname === '/superadmin/orders') return 'All Orders';
    if (location.pathname === '/superadmin/users') return 'Users Management';
    if (location.pathname === '/superadmin/brands') return 'Brands Management';
    if (location.pathname === '/superadmin/banners') return 'Banner Management';
    if (location.pathname === '/superadmin/categories') return 'Categories Management';
    if (location.pathname === '/superadmin/subcategories') return 'Subcategories Management';
    if (location.pathname === '/superadmin/shipping-countries') return 'Shipping Countries';
    if (location.pathname === '/superadmin/shipping-rates') return 'Shipping Rates';
    if (location.pathname === '/superadmin/status') return 'Status';
    if (location.pathname === '/superadmin/voucher-codes') return 'Voucher Codes';
    if (location.pathname === '/superadmin/payments') return 'Payments';
    if (location.pathname === '/superadmin/bank-details') return 'Bank Details';
    if (location.pathname === '/superadmin/settings') return 'System Settings';
    return 'Dashboard';
  };

  return (
    <AdminContainer>
      <Overlay $isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      <Sidebar $isOpen={sidebarOpen}>
        <SidebarHeader>
          <SidebarHeaderTop>
            <LogoBox>W</LogoBox>
            <UserInfo>
              <UserName>WASL</UserName>
              <UserStatus>Super Admin</UserStatus>
            </UserInfo>
            <MenuButton type="button" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
              <FiX />
            </MenuButton>
          </SidebarHeaderTop>
        </SidebarHeader>
        <Nav>
          <NavLink to="/superadmin" end>
            <FiGrid /> Dashboard
          </NavLink>
          <NavLink to="/superadmin/products">
            <FiPackage /> All Products
          </NavLink>
          <NavLink to="/superadmin/status">
            <FiTruck /> Status
          </NavLink>
          <NavLink to="/superadmin/orders">
            <FiShoppingCart /> All Orders
          </NavLink>
          <NavLink to="/superadmin/users">
            <FiUsers /> Users Management
          </NavLink>
          <NavLink to="/superadmin/brands">
            <FiTag /> Brands Management
          </NavLink>
          <NavLink to="/superadmin/banners">
            <FiImage /> Banner Management
          </NavLink>
          <NavLink to="/superadmin/categories">
            <FiLayers /> Categories
          </NavLink>
          <NavLink to="/superadmin/subcategories">
            <FiFolder /> Subcategories
          </NavLink>
          <NavLink to="/superadmin/shipping-countries">
            <FiGlobe /> Shipping Countries
          </NavLink>
          <NavLink to="/superadmin/shipping-rates">
            <FiTruck /> Shipping Rates
          </NavLink>
          <NavLink to="/superadmin/voucher-codes">
            <FiTag /> Voucher Codes
          </NavLink>
          <NavLink to="/superadmin/payments">
            <FiDollarSign /> Payments
          </NavLink>
          <NavLink to="/superadmin/bank-details">
            <FiCreditCard /> Bank Details
          </NavLink>
          <NavLink to="/superadmin/settings">
            <FiSettings /> Settings
          </NavLink>
        </Nav>
      </Sidebar>
      <MainContent>
        <TopBar>
          <TopBarLeft>
            <MobileMenuButton type="button" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <FiMenu />
            </MobileMenuButton>
            <PageTitle>
              <TitleText>{getPageTitle()}</TitleText>
              <SuperAdminBadge>Super Admin</SuperAdminBadge>
            </PageTitle>
          </TopBarLeft>
          <TopBarActions>
            <IconButton type="button" aria-label="Notifications">
              <FiBell />
            </IconButton>
            <UserProfile>
              <Avatar>{userInitials}</Avatar>
              <UserDetails>
                <UserNameTop>{profile?.first_name || 'Super Admin'}</UserNameTop>
                <UserEmail>{session?.user?.email}</UserEmail>
              </UserDetails>
            </UserProfile>
            <LogoutButton onClick={handleLogout} disabled={loggingOut}>
              <FiLogOut />
              <span>{loggingOut ? 'Logging out...' : 'Logout'}</span>
            </LogoutButton>
          </TopBarActions>
        </TopBar>
        <Content>
          <Outlet />
        </Content>
      </MainContent>
    </AdminContainer>
  );
}

